import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getFirst } from '#imports'
import {
  searchAwards,
  searchComplements,
  searchInitiatives,
  searchKitchens,
  searchLifeStories,
  searchLivings,
  searchMagazine,
  searchMaintenance,
  searchNews
} from '~/graphql/search'
import { getStoresByName } from '~/graphql/stores/deta-fetch'
import { getStoreLocatorTarget } from '~/utils/storeLocatorTarget'

const MIN_TERM_LENGTH = 2
const DEFAULT_LIMIT = 5

export const useSearchStore = defineStore('search', () => {
  const query = ref('')
  const loading = ref(false)
  const pristine = ref(true)
  const error = ref(null)
  const results = ref({
    stores: [],
    initiatives: [],
    kitchens: [],
    livings: [],
    complements: [],
    news: [],
    awards: [],
    magazine: [],
    lifeStories: [],
    maintenance: []
  })

  const reset = (clearQuery = false) => {
    if (clearQuery) {
      query.value = ''
    }
    results.value = {
      stores: [],
      initiatives: [],
      kitchens: [],
      livings: [],
      complements: [],
      news: [],
      awards: [],
      magazine: [],
      lifeStories: [],
      maintenance: []
    }
    loading.value = false
    error.value = null
    pristine.value = true
  }

  const mapStore = store => {
    if (!store?.slug) return null

    // Negozi senza scheda pubblicata (41% degli store sul sito it, quasi tutti
    // sugli altri): si punta direttamente allo store locator del loro
    // territorio, con la card evidenziata. Cosi' si evita il rimbalzo sulla
    // scheda, che in navigazione client-side lasciava la pagina bloccata.
    // Confronto esplicito con false: se un domani la query smettesse di
    // esporre il campo, si continua a puntare alla scheda (dove il middleware
    // fa comunque da rete) invece di dirottare al locator anche chi la scheda
    // ce l'ha.
    const hasNoDetailsPage = store.detailsPageEnabled === false
    const locatorTarget = hasNoDetailsPage ? getStoreLocatorTarget(store) : null
    const fallbackTarget = hasNoDetailsPage && !locatorTarget

    return {
      key: store.id ?? store.slug ?? store.title,
      title: store.title,
      city: store.city,
      storeName: store.storeName,
      storeLegalName: store.storeLegalName,
      logoSubtitle: store.logoSubtitle,
      isSpecialistSingleBrand: !!store.isSpecialistSingleBrand,
      province: getFirst(store.province)?.title,
      foreignCountry: getFirst(store.foreignCountry)?.title,
      subtitle: [ store.city, getFirst(store.province)?.title ].filter(Boolean).join(' • '),
      routeName: locatorTarget?.name ?? (fallbackTarget ? 'store-locator' : 'rivenditore-slug'),
      routeParams: locatorTarget?.params ?? (fallbackTarget ? {} : { slug: store.slug }),
      routeQuery: locatorTarget?.query ?? null,
      sourceId: 'stores',
      score: store.searchScore ?? 0
    }
  }

  const mapKitchen = entry => {
    if (!entry?.slug) return null
    const categoryTitle = (entry.kitchenType || []).map(type => type?.title).filter(Boolean)[0]
    return {
      key: entry.id ?? entry.slug ?? entry.title,
      title: entry.title,
      subtitle: categoryTitle,
      routeName: 'kitchens-slug',
      routeParams: { slug: entry.slug },
      sourceId: 'kitchens',
      score: entry.searchScore ?? 0
    }
  }

  const mapLiving = entry => {
    if (!entry?.slug) return null
    return {
      key: entry.id ?? entry.slug ?? entry.title,
      title: entry.title,
      subtitle: null,
      routeName: 'living-slug',
      routeParams: { slug: entry.slug },
      sourceId: 'livings',
      score: entry.searchScore ?? 0
    }
  }

  const mapComplement = item => {
    if (!item?.slug) return null
    return {
      key: item.id ?? item.slug ?? item.title,
      title: item.title,
      subtitle: (item.category || []).map(c => c.title).filter(Boolean).join(' • '),
      routeName: 'complements-slug',
      routeParams: { slug: item.slug },
      sourceId: 'complements',
      score: item.searchScore ?? 0
    }
  }

  const mapLifeStory = entry => {
    if (!entry?.slug) return null
    return {
      key: entry.id ?? entry.slug ?? entry.title ?? entry.firstname,
      title: entry.title || entry.firstname,
      subtitle: entry.city || null,
      routeName: 'life-stories-slug',
      routeParams: { slug: entry.slug },
      sourceId: 'lifeStories',
      score: entry.searchScore ?? 0
    }
  }

  const mapAward = entry => {
    if (!entry?.slug) return null
    return {
      key: entry.id ?? entry.slug ?? entry.title,
      title: entry.title,
      subtitle: entry.abstract || null,
      routeName: 'awards-slug',
      routeParams: { slug: entry.slug },
      sourceId: 'awards',
      score: entry.searchScore ?? 0
    }
  }

  const mapInitiative = entry => {
    if (!entry?.slug) return null
    return {
      key: entry.id ?? entry.slug ?? entry.title,
      title: entry.title,
      subtitle: entry.subtitle || null,
      routeName: 'initiatives-slug',
      routeParams: { slug: entry.slug },
      sourceId: 'initiatives',
      score: entry.searchScore ?? 0
    }
  }

  const mapMagazine = entry => {
    if (!entry?.slug) return null
    const category = (entry.category || [])[0]
    if (!category?.slug) return null
    return {
      key: entry.id ?? entry.slug ?? entry.title,
      title: entry.title,
      subtitle: category?.title || null,
      routeName: 'magazine-category-slug',
      routeParams: { slug: entry.slug, category: category.slug },
      sourceId: 'magazine',
      score: entry.searchScore ?? 0
    }
  }

  const mapNews = entry => {
    if (!entry?.slug) return null
    const category = (entry.categories || [])[0]
    if (!category?.slug) return null
    return {
      key: entry.id ?? entry.slug ?? entry.title,
      title: entry.title,
      subtitle: category?.title || null,
      routeName: 'news-category-slug',
      routeParams: { slug: entry.slug, category: category.slug },
      sourceId: 'news',
      score: entry.searchScore ?? 0
    }
  }

  const mapMaintenance = entry => {
    if (!entry?.slug) return null
    const category = (entry.categories || entry.maintenanceCategories || [])[0]
    if (!category?.slug) return null
    return {
      key: entry.id ?? entry.slug ?? entry.title,
      title: entry.title,
      subtitle: category?.title || null,
      routeName: 'maintenance-category-slug',
      routeParams: { slug: entry.slug, category: category.slug },
      sourceId: 'maintenance',
      score: entry.searchScore ?? 0
    }
  }

  const execute = async (limit = true) => {
    const term = (query.value || '').trim()
    if (!term || term.length < MIN_TERM_LENGTH) {
      reset()
      return []
    }

    loading.value = true
    error.value = null
    pristine.value = false

    const nextResults = {
      stores: [],
      initiatives: [],
      kitchens: [],
      livings: [],
      complements: [],
      news: [],
      awards: [],
      magazine: [],
      lifeStories: [],
      maintenance: []
    }

    try {
      const [
        stores,
        complements,
        kitchens,
        livings,
        lifeStories,
        awards,
        initiatives,
        magazine,
        news,
        maintenance
      ] = await Promise.all([
        getStoresByName(term),
        searchComplements(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchKitchens(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchLivings(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchLifeStories(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchAwards(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchInitiatives(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchMagazine(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchNews(term, { limit: limit ? DEFAULT_LIMIT : undefined }),
        searchMaintenance(term, { limit: limit ? DEFAULT_LIMIT : undefined })
      ])

      nextResults.kitchens = (kitchens || []).map(mapKitchen).filter(Boolean)
      nextResults.livings = (livings || []).map(mapLiving).filter(Boolean)
      nextResults.stores = (stores || []).map(mapStore).filter(Boolean)
      nextResults.complements = (complements || []).map(mapComplement).filter(Boolean)
      nextResults.lifeStories = (lifeStories || []).map(mapLifeStory).filter(Boolean)
      nextResults.awards = (awards || []).map(mapAward).filter(Boolean)
      nextResults.initiatives = (initiatives || []).map(mapInitiative).filter(Boolean)
      nextResults.magazine = (magazine || []).map(mapMagazine).filter(Boolean)
      nextResults.news = (news || []).map(mapNews).filter(Boolean)
      nextResults.maintenance = (maintenance || []).map(mapMaintenance).filter(Boolean)

      results.value = nextResults
      return nextResults
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  const flatResults = computed(() => {
    const aggregated = []
    const categoryOrder = [
      'stores',
      'initiatives',
      'kitchens',
      'livings',
      'complements',
      'news',
      'awards',
      'magazine',
      'lifeStories',
      'maintenance'
    ]
    const orderMap = categoryOrder.reduce((acc, id, idx) => {
      acc[id] = idx
      return acc
    }, {})

    Object.entries(results.value).forEach(([ sourceId, items ]) => {
      (items || []).forEach(item => {
        aggregated.push({
          ...item,
          sourceId
        })
      })
    })

    return aggregated.sort((a, b) => {
      const orderDiff = (orderMap[a.sourceId] ?? Infinity) - (orderMap[b.sourceId] ?? Infinity)
      if (orderDiff !== 0) return orderDiff

      const scoreDiff = (b.score ?? 0) - (a.score ?? 0)
      if (scoreDiff !== 0) return scoreDiff

      return (a.title || '').localeCompare(b.title || '')
    })
  })

  const groupedResults = computed(() => {
    const grouped = new Map()
    flatResults.value.forEach(item => {
      const bucket = grouped.get(item.sourceId) || []
      bucket.push(item)
      grouped.set(item.sourceId, bucket)
    })

    return Array.from(grouped.entries()).map(([ id, items ]) => ({
      id,
      items
    }))
  })

  const hasResults = computed(() => flatResults.value.length > 0)

  return {
    query,
    loading,
    pristine,
    error,
    results,
    flatResults,
    groupedResults,
    hasResults,
    execute,
    reset
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSearchStore, import.meta.hot))
}
